import React, { useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Modal from "../../components/UI/Modal";
import { createCategoryList } from "../ComponentContainer/createCategoryList";
import {
    bulkUploadProductsFromFolder,
    resetBulkUploadProductsFromFolder,
} from "../../actions/product.action";
import { getAdminProductModelData } from "../../actions/officeAction";
import apiClient from "../../helpers/axios";
import {
    disposeCatalogModelData,
    measureCatalogModelGeometry,
} from "../CatalogReview/catalogGeometryMeasurement";

const DEFAULT_CATEGORY_ID = "681485ee43c2083aab468f40";

const getGroupsPreview = (files) => {
    const groups = new Map();

    files.forEach((file) => {
        const relativePath = file.webkitRelativePath || file.name;
        const parts = relativePath.replace(/\\/g, "/").split("/").filter(Boolean);

        if (parts.length <= 1) return;

        const folderName = parts[parts.length - 2];

        if (!groups.has(folderName)) {
            groups.set(folderName, {
                folderName,
                model: null,
                images: 0,
            });
        }

        const ext = file.name.split(".").pop().toLowerCase();
        const group = groups.get(folderName);

        if (["glb", "gltf", "obj", "stl", "fbx"].includes(ext)) {
            group.model = file.name;
        }

        if (["jpg", "jpeg", "png", "webp"].includes(ext)) {
            group.images += 1;
        }
    });

    return Array.from(groups.values()).sort((a, b) => {
        return a.folderName.localeCompare(b.folderName, "ru", {
            numeric: true,
            sensitivity: "base",
        });
    });
};

const formatDimensions = (dimensions = {}) => {
    const height = Number(dimensions?.height || 0);
    const width = Number(dimensions?.width || 0);
    const thickness = Number(dimensions?.thickness || 0);

    if (!(height > 0) || !(width > 0) || !(thickness > 0)) {
        return "—";
    }

    return `${Math.round(height)}×${Math.round(width)}×${Math.round(thickness)} мм`;
};

const BulkProductUploadModal = ({ show, handleClose, slug }) => {
    const dispatch = useDispatch();
    const folderInputRef = useRef(null);
    const measuredImportRef = useRef("");

    const category = useSelector((state) => state.category);
    const {
        bulkUploadLoading,
        bulkUploadReport,
        bulkUploadMessage,
        bulkUploadError,
    } = useSelector((state) => state.product);

    const categoryList = useMemo(() => {
        return createCategoryList(category.categories || []);
    }, [category.categories]);

    const [files, setFiles] = useState([]);
    const [form, setForm] = useState({
        categoryId: DEFAULT_CATEGORY_ID,
        visibleProduct: "false",
        visibilityPrice: "public",
        visibleWatermark: "visible",
        addWhiteBackground: "visible",
        quantity: "0",
        price: "",
    });

    const [measurementState, setMeasurementState] = useState({
        running: false,
        current: 0,
        total: 0,
        success: 0,
        failed: 0,
        currentName: "",
        rows: [],
    });
    const [backfillState, setBackfillState] = useState({
        loading: false,
        message: "",
        error: "",
    });

    const groupsPreview = useMemo(() => getGroupsPreview(files), [files]);

    useEffect(() => {
        if (!show) {
            setFiles([]);
            measuredImportRef.current = "";
            setMeasurementState({
                running: false,
                current: 0,
                total: 0,
                success: 0,
                failed: 0,
                currentName: "",
                rows: [],
            });
            setBackfillState({
                loading: false,
                message: "",
                error: "",
            });
            dispatch(resetBulkUploadProductsFromFolder());
            if (folderInputRef.current) {
                folderInputRef.current.value = "";
            }
        }
    }, [show, dispatch]);

    useEffect(() => {
        const created = Array.isArray(bulkUploadReport?.created)
            ? bulkUploadReport.created
            : [];

        if (
            !show
            || bulkUploadLoading
            || !created.length
        ) {
            return undefined;
        }

        const signature = created
            .map((item) => String(item?._id || ""))
            .filter(Boolean)
            .sort()
            .join("|");

        if (!signature || measuredImportRef.current === signature) {
            return undefined;
        }

        measuredImportRef.current = signature;
        let cancelled = false;

        const run = async () => {
            let success = 0;
            let failed = 0;
            const rows = [];

            setMeasurementState({
                running: true,
                current: 0,
                total: created.length,
                success: 0,
                failed: 0,
                currentName: "",
                rows: [],
            });

            for (let index = 0; index < created.length; index += 1) {
                if (cancelled) break;

                const item = created[index];
                const productId = String(item?._id || "");
                let modelData = null;

                setMeasurementState((previous) => ({
                    ...previous,
                    current: index + 1,
                    currentName: item?.fullName || item?.name || productId,
                    success,
                    failed,
                }));

                if (!productId) {
                    failed += 1;
                    continue;
                }

                try {
                    const action = await dispatch(
                        getAdminProductModelData(
                            {
                                _id: productId,
                                fullName: item?.fullName || item?.name || "",
                            },
                            { cache: false }
                        )
                    );

                    modelData = action?.payload || null;

                    const measurement = measureCatalogModelGeometry({
                        modelData,
                        productDimensions: {},
                    });

                    const response = await apiClient.post(
                        `/admin/product/model/${encodeURIComponent(productId)}/measurement`,
                        {
                            measurement,
                            source: "bulk-upload-3d",
                            initializeBaseDimensions: true,
                        }
                    );

                    const dimensions = response?.data?.measurement?.dimensions
                        || measurement.dimensions;

                    success += 1;
                    rows.push({
                        productId,
                        name: item?.fullName || item?.name || productId,
                        ok: true,
                        dimensions,
                    });
                } catch (error) {
                    failed += 1;
                    rows.push({
                        productId,
                        name: item?.fullName || item?.name || productId,
                        ok: false,
                        message: (
                            error?.response?.data?.message
                            || error?.message
                            || "Не удалось получить размер 3D"
                        ),
                    });
                } finally {
                    disposeCatalogModelData(modelData);
                }

                if (!cancelled) {
                    setMeasurementState((previous) => ({
                        ...previous,
                        current: index + 1,
                        success,
                        failed,
                        rows: [...rows],
                    }));
                }
            }

            if (!cancelled) {
                setMeasurementState((previous) => ({
                    ...previous,
                    running: false,
                    current: created.length,
                    currentName: "",
                    success,
                    failed,
                    rows: [...rows],
                }));
            }
        };

        run().catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [
        bulkUploadLoading,
        bulkUploadReport,
        dispatch,
        show,
    ]);

    const handleFilesChange = (e) => {
        const selectedFiles = Array.from(e.target.files || []);
        setFiles(selectedFiles);
        measuredImportRef.current = "";
        setMeasurementState({
            running: false,
            current: 0,
            total: 0,
            success: 0,
            failed: 0,
            currentName: "",
            rows: [],
        });
        dispatch(resetBulkUploadProductsFromFolder());
    };

    const updateField = (name, value) => {
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const runImport = (dryRun) => {
        if (!form.categoryId) {
            alert("Выберите категорию");
            return;
        }

        if (!files.length) {
            alert("Выберите папку с товарами");
            return;
        }

        measuredImportRef.current = "";

        dispatch(bulkUploadProductsFromFolder({
            ...form,
            dryRun,
            files,
        }, slug));
    };

    const syncStoredCatalogMeasurements = async () => {
        if (backfillState.loading) return;

        setBackfillState({
            loading: true,
            message: "",
            error: "",
        });

        try {
            const response = await apiClient.post(
                "/admin/product/model-measurements/backfill"
            );

            setBackfillState({
                loading: false,
                message: response?.data?.message || "Размеры синхронизированы.",
                error: "",
            });
        } catch (error) {
            setBackfillState({
                loading: false,
                message: "",
                error: (
                    error?.response?.data?.message
                    || error?.message
                    || "Не удалось синхронизировать размеры"
                ),
            });
        }
    };

    return (
        <Modal
            size="lg"
            show={show}
            handleClose={handleClose}
            modalTitle="Массовый импорт товаров из папки"
            disabledFunctional
            buttons={[
                {
                    label: bulkUploadLoading ? "Проверка..." : "Проверить",
                    color: "secondary",
                    onClick: () => runImport(true),
                },
                {
                    label: bulkUploadLoading ? "Импорт..." : "Импортировать",
                    color: "dark",
                    onClick: () => runImport(false),
                },
                {
                    label: "Закрыть",
                    color: "light",
                    onClick: handleClose,
                },
            ]}
        >
            <Row>
                <Col md="8">
                    <Form.Group className="mb-3">
                        <Form.Label>Категория для загрузки</Form.Label>
                        <Form.Control
                            as="select"
                            value={form.categoryId}
                            onChange={(e) => updateField("categoryId", e.target.value)}
                        >
                            <option value="">Выбрать категорию</option>
                            {categoryList.map((item) => (
                                <option key={item.value} value={item.value}>
                                    {item.name}
                                </option>
                            ))}
                        </Form.Control>
                    </Form.Group>
                </Col>

                <Col md="4">
                    <Form.Group className="mb-3">
                        <Form.Label>Количество</Form.Label>
                        <Form.Control
                            type="number"
                            value={form.quantity}
                            onChange={(e) => updateField("quantity", e.target.value)}
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col md="4">
                    <Form.Group className="mb-3">
                        <Form.Label>Видимость товара</Form.Label>
                        <Form.Control
                            as="select"
                            value={form.visibleProduct}
                            onChange={(e) => updateField("visibleProduct", e.target.value)}
                        >
                            <option value="false">Только админ</option>
                            <option value="true">Публичный</option>
                        </Form.Control>
                    </Form.Group>
                </Col>

                <Col md="4">
                    <Form.Group className="mb-3">
                        <Form.Label>Видимость цены</Form.Label>
                        <Form.Control
                            as="select"
                            value={form.visibilityPrice}
                            onChange={(e) => updateField("visibilityPrice", e.target.value)}
                        >
                            <option value="private">Никому</option>
                            <option value="public">Публичный</option>
                            <option value="admin">Только админ</option>
                        </Form.Control>
                    </Form.Group>
                </Col>

                <Col md="4">
                    <Form.Group className="mb-3">
                        <Form.Label>Цена</Form.Label>
                        <Form.Control
                            type="number"
                            value={form.price}
                            placeholder="Можно оставить пустым"
                            onChange={(e) => updateField("price", e.target.value)}
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col md="6">
                    <Form.Group className="mb-3">
                        <Form.Label>Водный знак</Form.Label>
                        <Form.Control
                            as="select"
                            value={form.visibleWatermark}
                            onChange={(e) => updateField("visibleWatermark", e.target.value)}
                        >
                            <option value="visible">Наложить</option>
                            <option value="hide">Без знака</option>
                        </Form.Control>
                    </Form.Group>
                </Col>

                <Col md="6">
                    <Form.Group className="mb-3">
                        <Form.Label>Белый фон</Form.Label>
                        <Form.Control
                            as="select"
                            value={form.addWhiteBackground}
                            onChange={(e) => updateField("addWhiteBackground", e.target.value)}
                        >
                            <option value="visible">Добавить фон</option>
                            <option value="hide">Без фона</option>
                        </Form.Control>
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label>Выбрать папку с товарами</Form.Label>
                <input
                    ref={folderInputRef}
                    className="form-control"
                    type="file"
                    multiple
                    webkitdirectory="true"
                    directory="true"
                    onChange={handleFilesChange}
                />
            </Form.Group>

            {files.length > 0 ? (
                <div className="bulkUploadPreview">
                    <div className="bulkUploadPreview__head">
                        Выбрано файлов: {files.length}. Найдено папок: {groupsPreview.length}.
                    </div>

                    <div className="bulkUploadPreview__list">
                        {groupsPreview.map((group) => (
                            <div className="bulkUploadPreview__item" key={group.folderName}>
                                <strong>{group.folderName}</strong>
                                <span>Модель: {group.model || "не найдена"}</span>
                                <span>Фото: {group.images}</span>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {bulkUploadLoading ? (
                <div className="bulkUploadResult">
                    Выполняется обработка...
                </div>
            ) : null}

            {bulkUploadMessage ? (
                <div className="bulkUploadResult">
                    {bulkUploadMessage}
                </div>
            ) : null}

            {bulkUploadError ? (
                <div className="bulkUploadResult bulkUploadResult--error">
                    {bulkUploadError?.message || "Ошибка импорта"}
                </div>
            ) : null}

            {measurementState.total > 0 ? (
                <div className="bulkUploadReport">
                    <div>
                        <strong>Автоматический замер 3D:</strong>{" "}
                        {measurementState.current}/{measurementState.total}
                    </div>
                    <div>Получено размеров: {measurementState.success}</div>
                    <div>Ошибок замера: {measurementState.failed}</div>
                    {measurementState.running && measurementState.currentName ? (
                        <div>Сейчас: {measurementState.currentName}</div>
                    ) : null}

                    {measurementState.rows.length ? (
                        <div className="bulkUploadReport__section">
                            <strong>Размеры моделей:</strong>
                            {measurementState.rows.map((row) => (
                                <div key={row.productId}>
                                    {row.name} —{" "}
                                    {row.ok
                                        ? formatDimensions(row.dimensions)
                                        : `ошибка: ${row.message}`}
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {bulkUploadReport ? (
                <div className="bulkUploadReport">
                    <div>Папок: {bulkUploadReport.totalFolders}</div>
                    <div>Готово: {bulkUploadReport.ready?.length || 0}</div>
                    <div>Создано: {bulkUploadReport.created?.length || 0}</div>
                    <div>Пропущено: {bulkUploadReport.skipped?.length || 0}</div>
                    <div>Ошибок: {bulkUploadReport.errors?.length || 0}</div>

                    {bulkUploadReport.created?.length ? (
                        <div className="bulkUploadReport__section">
                            <strong>Созданные товары:</strong>
                            {bulkUploadReport.created.map((item) => (
                                <div key={item._id}>
                                    {item.fullName} — {item.folder}
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {bulkUploadReport.ready?.length ? (
                        <div className="bulkUploadReport__section">
                            <strong>Готово к созданию:</strong>
                            {bulkUploadReport.ready.map((item) => (
                                <div key={`${item.folder}-${item.fullName}`}>
                                    {item.fullName} — {item.folder}
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {bulkUploadReport.skipped?.length ? (
                        <div className="bulkUploadReport__section">
                            <strong>Пропущено:</strong>
                            {bulkUploadReport.skipped.map((item) => (
                                <div key={item.folder}>
                                    {item.fullName} — {item.folder} — {item.message}
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {bulkUploadReport.errors?.length ? (
                        <div className="bulkUploadReport__section bulkUploadReport__section--error">
                            <strong>Ошибки:</strong>
                            {bulkUploadReport.errors.map((item) => (
                                <div key={item.folder}>
                                    {item.folder} — {item.message}
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}

            <div className="d-flex flex-wrap gap-2">
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                        setFiles([]);
                        measuredImportRef.current = "";
                        dispatch(resetBulkUploadProductsFromFolder());
                        if (folderInputRef.current) {
                            folderInputRef.current.value = "";
                        }
                    }}
                >
                    Очистить выбранную папку
                </Button>

                <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={backfillState.loading || measurementState.running}
                    onClick={syncStoredCatalogMeasurements}
                >
                    {backfillState.loading
                        ? "Синхронизация..."
                        : "Подтянуть измеренные 3D-размеры"}
                </Button>
            </div>

            {backfillState.message ? (
                <div className="bulkUploadResult">
                    {backfillState.message}
                </div>
            ) : null}

            {backfillState.error ? (
                <div className="bulkUploadResult bulkUploadResult--error">
                    {backfillState.error}
                </div>
            ) : null}
        </Modal>
    );
};

export default BulkProductUploadModal;
